import { useEffect, useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useLang } from "@/i18n";
import { scrollToId } from "@/lib/site";
import { LangSwitcher } from "./LangSwitcher";
import { ShineButton } from "./ShineButton";

const LINKS = ["historia", "carta", "vision", "galeria"];
const SERVICES = ["cumpleanos", "fiestas", "reservas"];

export const openService = (id) => {
  window.dispatchEvent(new CustomEvent("open-service", { detail: id }));
  scrollToId("servicios");
};

const Logo = () => (
  <a href="/" data-testid="header-logo" className="flex items-center gap-3 group" aria-label="El gato con copas - inicio">
    <img src="/sello-gato-sm.jpg" alt="El gato con copas logo" className="h-12 w-12 sm:h-14 sm:w-14 rounded-xl object-cover border-2 border-jungle shadow-md transition-transform duration-500 group-hover:rotate-[-6deg]" />
    <span className="font-brand text-lg sm:text-2xl leading-none text-jungle">El gato<br className="sm:hidden" /> con copas</span>
  </a>
);

export const Header = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => { setOpen(false); scrollToId(id); };

  return (
    <header data-testid="site-header" className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,padding] duration-500 ${scrolled ? "bg-paper/85 backdrop-blur-xl shadow-[0_10px_30px_-20px_rgba(8,26,19,0.5)] py-2" : "bg-paper/70 backdrop-blur-md py-4"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4">
        <Logo />
        <nav className="hidden lg:flex items-center gap-7 text-[13px] font-semibold">
          {LINKS.slice(0, 3).map((id) => (
            <button key={id} data-testid={`nav-${id}`} onClick={() => go(id)} className="hover:text-mango transition-colors">{t.nav[id]}</button>
          ))}
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger data-testid="nav-servicios-dropdown" className="inline-flex items-center gap-1 hover:text-mango transition-colors outline-none">
              {t.nav.servicios} <ChevronDown size={14} />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="rounded-2xl bg-paper border-jungle/15">
              {SERVICES.map((s) => (
                <DropdownMenuItem key={s} data-testid={`nav-servicio-${s}`} onClick={() => openService(s)} className="cursor-pointer rounded-xl">
                  {t.services[s]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <button data-testid="nav-galeria" onClick={() => go("galeria")} className="hover:text-mango transition-colors">{t.nav.galeria}</button>
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <LangSwitcher />
          <ShineButton testId="header-cta" onClick={() => go("contacto")} className="hidden md:inline-flex !py-3 !px-5">{t.nav.cta}</ShineButton>
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="lg:hidden rounded-full border border-jungle/20 p-2.5" aria-label="Menú">
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden mx-4 mt-3 rounded-3xl bg-jungle text-paper p-6 flex flex-col gap-4">
          {LINKS.map((id) => (
            <button key={id} data-testid={`mobile-nav-${id}`} onClick={() => go(id)} className="text-left font-display text-2xl">{t.nav[id]}</button>
          ))}
          <p className="font-mono-ed text-xs uppercase tracking-[0.25em] text-banana mt-2">{t.nav.servicios}</p>
          {SERVICES.map((s) => (
            <button key={s} data-testid={`mobile-servicio-${s}`} onClick={() => { setOpen(false); openService(s); }} className="text-left text-lg pl-3 border-l border-paper/25">{t.services[s]}</button>
          ))}
          <ShineButton testId="mobile-cta" onClick={() => go("contacto")} className="mt-2 w-full">{t.nav.cta}</ShineButton>
        </div>
      )}
    </header>
  );
};
